import React, { useLayoutEffect, useMemo, useRef } from 'react';
import Lenis from '@studio-freight/lenis';
import { motion, useMotionTemplate, useMotionValue, useSpring } from 'framer-motion';
import { AlertTriangle, ArrowRight, Eye, Search, ShieldCheck, Sparkles, Terminal } from 'lucide-react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Link } from 'react-router-dom';

gsap.registerPlugin(ScrollTrigger);

const products = [
  {
    eyebrow: 'Newest Launch',
    name: 'Bastion Security',
    subtitle: 'IDE extension for Web3 developers',
    description:
      'Detect private keys, seed phrases, and malicious dependency risks before code leaves your machine. Bastion runs directly inside VS Code and keeps scanning local, fast, and deployment-ready.',
    detail:
      'Built for fast-moving Web3 teams that need local, practical security guardrails.',
    icon: ShieldCheck,
    href: 'https://open-vsx.org/extension/safeedges/bastion-security',
    cta: 'Explore Bastion',
    accent: '#00A99E',
    bgClass:
      'bg-[radial-gradient(circle_at_18%_28%,rgba(0,169,158,0.2),transparent_45%),radial-gradient(circle_at_86%_72%,rgba(45,212,191,0.16),transparent_42%),linear-gradient(145deg,#0b0f14,#080d12)]',
    highlights: ['Local-first secret scanning', 'AI diff credential leak detection', 'Supply-chain firewall'],
    bullets: [
      'Secrets flagged directly in editor',
      'Supply-chain risk detection',
      'PR-ready remediation hints',
    ],
    metrics: [
      { value: 'Local', label: 'Code never leaves machine' },
      { value: '14+', label: 'Secret categories detected' },
      { value: 'Real-time', label: 'Inline developer feedback' },
    ],
  },
  {
    eyebrow: 'Fuel Ecosystem',
    name: 'SwayScan',
    subtitle: 'Static analyzer for Sway contracts',
    description:
      'AST-driven analysis for Fuel smart contracts with audit-grade findings. Catch access-control and state-flow issues before they become production incidents.',
    detail:
      'Designed for protocol teams that want stronger security baselines before formal audits.',
    icon: Search,
    href: 'https://crates.io/crates/swayscan/',
    cta: 'Try SwayScan',
    accent: '#22d3ee',
    bgClass:
      'bg-[radial-gradient(circle_at_20%_22%,rgba(34,211,238,0.2),transparent_42%),radial-gradient(circle_at_80%_76%,rgba(59,130,246,0.16),transparent_45%),linear-gradient(145deg,#0a0f18,#09101a)]',
    highlights: ['FuelVM-focused checks', 'AST precision engine', 'Markdown/PDF exports'],
    bullets: [
      'AST-based Sway parsing',
      'Access/state risk flags',
      'Clear remediation reports',
    ],
    metrics: [
      { value: 'Sway', label: 'Language specialization' },
      { value: 'AST', label: 'Analysis foundation' },
      { value: 'CLI', label: 'Developer workflow ready' },
    ],
  },
  {
    eyebrow: 'On-chain Intelligence',
    name: 'EVM Sleuth',
    subtitle: 'Real-time blockchain threat monitoring',
    description:
      'Turn live EVM activity into actionable alerts. Detect suspicious approvals, malicious contract interactions, and transaction behavior in real time.',
    detail:
      'Built for responders and researchers that need clear, real-time risk signals.',
    icon: Eye,
    href: 'https://evmsleuth.com',
    cta: 'Open EVM Sleuth',
    accent: '#c084fc',
    bgClass:
      'bg-[radial-gradient(circle_at_14%_30%,rgba(168,85,247,0.2),transparent_42%),radial-gradient(circle_at_82%_76%,rgba(236,72,153,0.16),transparent_45%),linear-gradient(145deg,#120a16,#0e0a12)]',
    highlights: ['Live mempool signals', 'Threat scoring pipeline', 'Instant risk alerts'],
    bullets: [
      'Live transaction intelligence',
      'Exploit pattern detection',
      'Priority incident alerts',
    ],
    metrics: [
      { value: '24/7', label: 'Continuous monitoring' },
      { value: 'EVM', label: 'Chain family coverage' },
      { value: 'Live', label: 'Alerting pipeline' },
    ],
  },
];

const ProductLink = ({ href, children, className }) => {
  if (href.startsWith('http')) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
        {children}
      </a>
    );
  }
  return (
    <Link to={href} className={className}>
      {children}
    </Link>
  );
};

const ShowcaseVisual = ({ accent, name, subtitle, highlights, metrics }) => (
  <div className="relative h-[320px] w-full overflow-hidden rounded-[1.75rem] border border-white/15 bg-white/[0.05] p-4 sm:h-[390px]">
    <motion.div
      className="absolute -right-16 -top-16 h-44 w-44 rounded-full blur-2xl"
      style={{ backgroundColor: accent, opacity: 0.28 }}
      animate={{ scale: [1, 1.08, 1], opacity: [0.22, 0.34, 0.22] }}
      transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
    />
    <motion.div
      className="absolute -bottom-20 -left-20 h-52 w-52 rounded-full bg-white/15 blur-2xl"
      animate={{ scale: [1, 1.12, 1], opacity: [0.12, 0.22, 0.12] }}
      transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
    />

    <div className="relative h-full rounded-2xl border border-white/15 bg-black/35 p-4 sm:p-6">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
          <span className="h-2.5 w-2.5 rounded-full bg-yellow-400" />
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
        </div>
        <p className="text-[11px] uppercase tracking-[0.22em] text-gray-500">Live security console</p>
      </div>

      <div className="mb-4 rounded-xl border border-white/15 bg-white/[0.06] p-3">
        <p className="text-xs uppercase tracking-[0.2em] text-gray-500">Product surface</p>
        <p className="mt-1 text-base font-semibold text-white">{name}</p>
        <p className="text-xs text-gray-400">{subtitle}</p>
      </div>

      <div className="space-y-2.5">
        {highlights.map((item) => (
          <div key={item} className="flex items-start gap-3 rounded-xl border border-white/15 bg-white/[0.06] px-3 py-2">
            <Terminal className="mt-0.5 h-4 w-4 text-gray-300" />
            <p className="text-sm text-gray-300">{item}</p>
          </div>
        ))}
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2">
        {metrics.map((metric) => (
          <div key={metric.label} className="rounded-lg border border-white/15 bg-black/30 px-2 py-2">
            <p className="truncate text-[11px] font-semibold text-white">{metric.value}</p>
            <p className="truncate text-[10px] text-gray-500">{metric.label}</p>
          </div>
        ))}
      </div>

      <div className="mt-4 space-y-2">
        {[68, 82, 54].map((width, idx) => (
          <div key={idx} className="h-1.5 overflow-hidden rounded-full bg-white/10">
            <motion.div
              className="h-full rounded-full"
              style={{ backgroundColor: accent }}
              initial={{ width: '0%' }}
              animate={{ width: `${width}%` }}
              transition={{ duration: 0.8, delay: idx * 0.08, ease: 'easeOut' }}
            />
          </div>
        ))}
      </div>

      <motion.div
        className="absolute bottom-4 right-4 rounded-xl border border-white/15 bg-white/[0.04] px-3 py-2 text-xs text-gray-300"
        animate={{ y: [0, -6, 0] }}
        transition={{ duration: 3.2, repeat: Infinity, ease: 'easeInOut' }}
      >
        Threats filtered in real time
      </motion.div>
    </div>
  </div>
);

const ProductCard = ({ product, setCardRef }) => {
  const Icon = product.icon;
  return (
    <article
      ref={setCardRef}
      className="absolute inset-0 flex items-center justify-center px-4 pb-12 pt-32 will-change-transform sm:px-8 md:pt-36 lg:px-12"
    >
      <div className="relative w-full max-w-7xl overflow-hidden rounded-[2rem] border border-white/15 bg-black/45 shadow-[0_30px_80px_rgba(0,0,0,0.45)]">
        <div className="grid min-h-[76vh] grid-cols-1 items-center gap-10 p-6 md:p-10 lg:grid-cols-[1fr_1.05fr] lg:p-14">
          <div className="space-y-6">
            <p data-reveal className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/40 px-4 py-2 text-xs uppercase tracking-[0.22em] text-gray-300">
              <Sparkles className="h-3.5 w-3.5" style={{ color: product.accent }} />
              {product.eyebrow}
            </p>

            <div data-reveal className="flex items-start gap-4">
              <div className="grid h-14 w-14 place-items-center rounded-2xl border border-white/15 bg-black/35">
                <Icon className="h-7 w-7" style={{ color: product.accent }} />
              </div>
              <div>
                <h3 className="text-4xl font-semibold tracking-tight text-white sm:text-5xl lg:text-[3.4rem]">{product.name}</h3>
                <p className="mt-2 text-sm font-medium sm:text-base" style={{ color: product.accent }}>
                  {product.subtitle}
                </p>
              </div>
            </div>

            <p data-reveal className="max-w-xl text-base leading-7 text-gray-300 sm:text-lg">
              {product.description}
            </p>

            <p data-reveal className="max-w-xl text-sm leading-6 text-gray-400 sm:text-[15px]">
              {product.detail}
            </p>

            <ul data-reveal className="grid max-w-xl grid-cols-1 gap-2 text-sm text-gray-300 sm:grid-cols-2">
              {product.bullets.map((item) => (
                <li key={item} className="flex items-center gap-2.5 rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2">
                  <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: product.accent }} />
                  <span className="leading-5">{item}</span>
                </li>
              ))}
            </ul>

            <div data-reveal className="grid max-w-xl grid-cols-3 gap-2.5">
              {product.metrics.map((metric) => (
                <div key={metric.label} className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2.5">
                  <p className="text-sm font-semibold text-white sm:text-base">{metric.value}</p>
                  <p className="text-[11px] text-gray-400 sm:text-xs">{metric.label}</p>
                </div>
              ))}
            </div>

            <div data-reveal className="pt-3">
              <ProductLink
                href={product.href}
                className="pointer-events-auto group inline-flex items-center gap-3 rounded-xl border border-white/30 px-6 py-3.5 text-sm font-bold text-white transition-all duration-300 hover:-translate-y-0.5 hover:brightness-110"
                style={{
                  background: `linear-gradient(135deg, ${product.accent} 0%, rgba(8,12,18,0.92) 180%)`,
                  boxShadow: `0 12px 30px ${product.accent}55`,
                }}
              >
                {product.cta}
                <span className="grid h-5 w-5 place-items-center rounded-full bg-white/15">
                  <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                </span>
              </ProductLink>
            </div>
          </div>

          <div data-reveal className="relative">
            <ShowcaseVisual
              accent={product.accent}
              name={product.name}
              subtitle={product.subtitle}
              highlights={product.highlights}
              metrics={product.metrics}
            />
          </div>
        </div>
      </div>
    </article>
  );
};

const ProductsScrollShowcase = () => {
  const sectionRef = useRef(null);
  const progressRef = useRef(null);
  const cardRefs = useRef([]);
  const bgRefs = useRef([]);
  const lenisRef = useRef(null);
  const ownsLenisRef = useRef(false);

  const pointerX = useMotionValue(50);
  const pointerY = useMotionValue(50);
  const smoothX = useSpring(pointerX, { stiffness: 120, damping: 20, mass: 0.24 });
  const smoothY = useSpring(pointerY, { stiffness: 120, damping: 20, mass: 0.24 });
  const glow = useMotionTemplate`radial-gradient(circle at ${smoothX}% ${smoothY}%, rgba(255,255,255,0.16), transparent 36%)`;

  const totalSteps = useMemo(() => Math.max(products.length - 1, 1), []);

  useLayoutEffect(() => {
    const isMobile = window.matchMedia('(max-width: 1024px)').matches;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const shouldUseLenis = !isMobile && !prefersReducedMotion;

    const previousScrollBehavior = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = 'auto';

    let ticker = null;

    if (shouldUseLenis && !window.__safeedgesLenis) {
      const lenis = new Lenis({
        lerp: 0.085,
        smoothWheel: true,
        normalizeWheel: true,
      });
      window.__safeedgesLenis = lenis;
      lenisRef.current = lenis;
      ownsLenisRef.current = true;

      ticker = (time) => {
        lenis.raf(time * 1000);
      };
      lenis.on('scroll', ScrollTrigger.update);
      gsap.ticker.add(ticker);
      gsap.ticker.lagSmoothing(0);
    } else {
      lenisRef.current = window.__safeedgesLenis || null;
    }

    const ctx = gsap.context(() => {
      const cards = cardRefs.current.filter(Boolean);
      const bgs = bgRefs.current.filter(Boolean);
      if (!cards.length) return;

      gsap.set(cards, { xPercent: 100, autoAlpha: 0, scale: 0.95, willChange: 'transform,opacity' });
      gsap.set(cards[0], { xPercent: 0, autoAlpha: 1, scale: 1 });
      gsap.set(bgs, { autoAlpha: 0 });
      gsap.set(bgs[0], { autoAlpha: 1 });
      gsap.set(progressRef.current, { scaleX: 0, transformOrigin: 'left center' });
      const setProgress = gsap.quickSetter(progressRef.current, 'scaleX');

      gsap.fromTo(
        cards[0].querySelectorAll('[data-reveal]'),
        { y: 24, autoAlpha: 0 },
        { y: 0, autoAlpha: 1, stagger: 0.08, duration: 0.7, ease: 'power2.out' }
      );

      const timeline = gsap.timeline({
        defaults: { ease: 'none' },
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top top',
          end: () => `+=${window.innerHeight * (products.length * (isMobile ? 1.15 : 1.25))}`,
          pin: true,
          scrub: isMobile ? 0.3 : 0.45,
          anticipatePin: 1,
          fastScrollEnd: true,
          invalidateOnRefresh: true,
          onUpdate: (self) => {
            setProgress(self.progress);
          },
        },
      });

      for (let i = 0; i < cards.length - 1; i += 1) {
        const label = `transition-${i}`;
        timeline.addLabel(label);
        timeline.to(cards[i], { xPercent: -20, autoAlpha: 0, scale: 0.9, duration: 1 }, label);
        timeline.fromTo(cards[i + 1], { xPercent: 100, autoAlpha: 0, scale: 0.95 }, { xPercent: 0, autoAlpha: 1, scale: 1, duration: 1 }, label);
        timeline.to(bgs[i], { autoAlpha: 0, duration: 1 }, label);
        timeline.to(bgs[i + 1], { autoAlpha: 1, duration: 1 }, label);
        timeline.fromTo(
          cards[i + 1].querySelectorAll('[data-reveal]'),
          { y: 26, autoAlpha: 0 },
          { y: 0, autoAlpha: 1, stagger: 0.08, duration: 0.56, ease: 'power2.out' },
          `${label}+=0.16`
        );
      }
    }, sectionRef);

    return () => {
      ctx.revert();
      if (ownsLenisRef.current && lenisRef.current) {
        lenisRef.current.off('scroll', ScrollTrigger.update);
        if (ticker) gsap.ticker.remove(ticker);
        lenisRef.current.destroy();
        window.__safeedgesLenis = null;
      }
      document.documentElement.style.scrollBehavior = previousScrollBehavior;
      lenisRef.current = null;
      ownsLenisRef.current = false;
    };
  }, []);

  const onMouseMove = (event) => {
    const bounds = sectionRef.current?.getBoundingClientRect();
    if (!bounds) return;
    const x = ((event.clientX - bounds.left) / bounds.width) * 100;
    const y = ((event.clientY - bounds.top) / bounds.height) * 100;
    pointerX.set(Math.min(100, Math.max(0, x)));
    pointerY.set(Math.min(100, Math.max(0, y)));
  };

  return (
    <section ref={sectionRef} onMouseMove={onMouseMove} className="relative h-screen overflow-hidden bg-black">
      <div className="absolute inset-0">
        {products.map((product, index) => (
          <div key={product.name} ref={(node) => { bgRefs.current[index] = node; }} className={`absolute inset-0 ${product.bgClass}`} />
        ))}
        <motion.div className="absolute inset-0 pointer-events-none hidden md:block" style={{ backgroundImage: glow }} />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.85)_0%,rgba(0,0,0,0.35)_40%,rgba(0,0,0,0.82)_100%)]" />
      </div>

      <div className="absolute inset-x-0 top-0 z-30 border-b border-white/10 bg-black/70">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1">
              <span className="h-1.5 w-1.5 rounded-full bg-[#00A99E]" />
              <p className="text-[10px] uppercase tracking-[0.28em] text-[#00A99E]">Product Story</p>
            </div>
            <h2 className="mt-2 text-xl font-semibold text-white sm:text-2xl">
              Built by auditors,
              <span className="ml-2 bg-gradient-to-r from-cyan-300 via-[#00A99E] to-fuchsia-300 bg-clip-text text-transparent">
                crafted for builders
              </span>
            </h2>
          </div>
          <p className="hidden text-right text-[11px] uppercase tracking-[0.2em] text-gray-400 md:block">
            Scroll to control the narrative
          </p>
        </div>
        <div className="h-[2px] bg-white/10">
          <div ref={progressRef} className="h-full origin-left scale-x-0 bg-gradient-to-r from-[#00A99E] via-cyan-400 to-fuchsia-500" />
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 z-30 hidden -translate-x-1/2 items-center gap-2 rounded-full border border-white/15 bg-black/70 px-4 py-2 text-xs text-gray-300 md:flex">
        <AlertTriangle className="h-3.5 w-3.5 text-amber-300" />
        {totalSteps + 1} chapters · reverse on scroll up
      </div>

      <div className="relative z-20 h-full">
        {products.map((product, index) => (
          <ProductCard
            key={product.name}
            product={product}
            setCardRef={(node) => {
              cardRefs.current[index] = node;
            }}
          />
        ))}
      </div>
    </section>
  );
};

const SecurityAuditSection = () => <ProductsScrollShowcase />;

export default SecurityAuditSection;
